import { useState } from 'react';
import { Button, LoadingStatus, Notice, buttonClass, cx } from '../../ui';
import { Link } from '../../router/Link';
import { PlayerSurface, StageFailure } from '../watch/view';
import type { WatchBinding } from '../watch/useWatchScreen';
import { AddToPlaylist } from '../../playlists/AddToPlaylist';
import { WatchLaterButton } from '../../playlists/WatchLaterButton';
import { useEmbedLanding, type EmbedBinding, type EmbedListTarget, type EmbedStep } from './model';
import { useCopy } from '../../strings/useCopy';
import styles from './embed.module.css';

/**
 * The /embed screen (2026-08-02): the player alone, wearing the whole viewport — no shell,
 * no metadata, no rail. It reuses the watch pipeline (`useWatchScreen`, minus comments) rather than
 * growing its own, so resume positions, history mirroring, SponsorBlock and the keymap behave
 * exactly as they do on /watch. What it adds is one band over the top edge — a clickable title at
 * one end, and at the other the two steps through the list, Save to playlist and Watch later
 * (`TopBand`).
 *
 * The failure states are the watch page's own `StageFailure` (2026-08-02) — message centered
 * on both axes, the Watch on menu, and Try again where the failure could clear on its own — drawn
 * straight onto the black viewport, which plays the part the player box plays on /watch. The band
 * rides above it there too rather than only inside the player surface: some failures here are
 * specific to being embedded cross-site (a browser blocking local-network or storage access on the
 * frame, see `docs/instance-setup.md`) and go away once the reader opens the real /watch page
 * instead, which is exactly what the title links to. **Above** is the word for it in both senses
 * (2026-09-14): the band clears a failed load's error box in the stacking order as well, and stays
 * up for as long as one is on screen.
 *
 * What the embed URL asked for reaches here already decided (`model.ts`): the bar's three parameters
 * are props on the surface, and everything else has been handed to the player behind it.
 */
export function EmbedView({ embed }: { embed: EmbedBinding }) {
  const words = useCopy().text.watch.page;
  const { binding } = embed;
  const { stage, videoRef, externalFrameRef, playAttempt } = binding;
  const title = binding.video?.title ?? null;
  const watchHref = embed.watchHref;
  // `color=white` is a custom property on the frame, which every arm wears — the bar reads it from
  // wherever it ends up, and on the arms that draw no bar it costs nothing.
  const viewport = cx(styles.viewport, embed.whiteProgress && styles.whiteProgress);

  /*
   * The player swap's iframe legs (YouTube always, PeerTube's "embedded" mode), checked ahead of
   * `stage.kind` exactly as the watch page's `Stage` checks them and for the same reason: the
   * reader who switched away from a *failed* load has to get the working player, not the error box
   * that offered them the switch. Missing here until 2026-09-14, which made both swap rows on
   * /embed do nothing at all — the state changed behind a view that never read it, so a live stream
   * the instance could not serve left the reader with an error box and a menu that answered it with
   * silence. The band keeps its row over the frame: this is a foreign player, so the title is the
   * only thing on screen naming what is playing and the only way back to the real page — and a row
   * rather than a layer, since a permanent band would sit on the strip that player draws its own
   * title and controls in.
   */
  if (binding.external !== null) {
    return (
      <div className={viewport}>
        {title === null ? null : (
          <TopBand
            binding={binding}
            title={title}
            href={watchHref}
            step={embed.step}
            visible
            flow
          />
        )}
        <iframe
          className={styles.externalFrame}
          src={binding.external.url}
          ref={externalFrameRef}
          title={words.externalFrameTitle[binding.external.provider]}
          allow="autoplay; encrypted-media; fullscreen; picture-in-picture"
          allowFullScreen
        />
      </div>
    );
  }

  switch (stage.kind) {
    case 'loading':
      return (
        <div className={cx(viewport, styles.centred)} aria-busy>
          <LoadingStatus>{words.loadingVideo}</LoadingStatus>
        </div>
      );

    case 'problem':
    case 'unplayable':
      return (
        <div className={viewport}>
          {title === null ? null : (
            <TopBand
              binding={binding}
              title={title}
              href={watchHref}
              step={embed.step}
              visible
              flow
            />
          )}
          <div className={styles.centred}>
            <StageFailure
              binding={binding}
              title={stage.title}
              detail={stage.detail}
              retry={
                stage.kind === 'problem' && stage.retryable
                  ? { onRetry: binding.handlers.retry, busy: binding.retrying }
                  : null
              }
            />
          </div>
        </div>
      );

    case 'upcoming':
      return (
        <div className={viewport}>
          {title === null ? null : (
            <TopBand
              binding={binding}
              title={title}
              href={watchHref}
              step={embed.step}
              visible
              flow
            />
          )}
          <div className={styles.centred}>
            <StageFailure
              binding={binding}
              title={words.premiereHeading}
              detail={
                stage.premiereText === null
                  ? words.premiereUnknown
                  : words.premiereAt(stage.premiereText)
              }
              retry={null}
            />
          </div>
        </div>
      );

    case 'progressive':
      return (
        <div className={viewport}>
          {/* No chrome on the no-MSE arm, so there is no fade clock — the band just stays. */}
          {title === null ? null : (
            <TopBand
              binding={binding}
              title={title}
              href={watchHref}
              step={embed.step}
              visible
              flow
            />
          )}
          <video
            className={styles.progressiveVideo}
            ref={videoRef}
            src={stage.url}
            controls
            playsInline
          />
          <div className={styles.notices}>
            <Notice>{stage.notice}</Notice>
          </div>
        </div>
      );

    case 'player':
      return (
        <div className={viewport}>
          {/* No theater toggle here: the surface already wears the whole viewport, so the mode
              would only silently reshape the reader's /watch layout. The widen control goes with it
              for a different reason — this box is the embedder's and the surface opts out of the
              watch page's geometry, so there is no stage of ours for a crop to act on. A failed load's error renders
              inside the surface (`PlayerSurface`'s overlay), like every other failure here. The
              fullscreen button is the embedder's call (`fs=0`), and `controls=0` takes the whole
              bar — the band above it stays either way, since it is the reader's way to the real
              page and the only thing on screen that says what they are watching. */}
          <PlayerSurface
            binding={binding}
            surfaceClassName={styles.surface}
            bar={embed.bar}
            chromeOptions={{
              theater: false,
              chapterListing: false,
              fullscreen: embed.fullscreenButton,
              widen: false,
            }}
            overlay={
              title === null ? null : (
                <TopBand
                  binding={binding}
                  title={title}
                  href={watchHref}
                  step={embed.step}
                  /* Pinned up while a load has failed: the error box takes the picture, so there is
                     no video for the auto-hide to be getting out of the way of, and the band is
                     what the reader needs most right then — the way to the real page, and Watch
                     later on a video they came here to keep. */
                  visible={binding.chrome.visible || binding.playerError !== null}
                />
              )
            }
          />
          <div className={styles.notices}>
            {playAttempt === 'blocked' ? <Notice live>{words.playBlocked}</Notice> : null}
            {playAttempt === 'muted-fallback' ? <Notice live>{words.mutedFallback}</Notice> : null}
          </div>
        </div>
      );
  }
}

/**
 * `/embed/?list=PL…` — the address that names a playlist and no video, while it works out which
 * video that is.
 *
 * It is a screen rather than a spinner inside the player because there is no player yet: which
 * entry a list starts at is the playlist's answer to give, and until it arrives there is nothing to
 * build one from. The usual case is one request long and the reader sees this for a moment; what it
 * owes them is the other case, where the playlist is private, gone or empty, since the frame would
 * otherwise sit black in somebody else's page with nothing saying why. **Open this playlist** is
 * the way out, in a tab of its own for the title link's reason: this page is in an iframe.
 */
export function EmbedLandingView({ target }: { readonly target: EmbedListTarget }) {
  const words = useCopy().text.watch.page;
  const landing = useEmbedLanding(target);

  if (landing.loading) {
    return (
      <div className={cx(styles.viewport, styles.centred)} aria-busy>
        <LoadingStatus>{words.listLoading}</LoadingStatus>
      </div>
    );
  }

  return (
    <div className={cx(styles.viewport, styles.centred)}>
      <div className={styles.landing}>
        <Notice>{landing.problem}</Notice>
        {/* A plain anchor, like the band's title link and for the same reason: this page is in
            somebody else's iframe, and navigating it would cram the whole app into their box. */}
        <a className={styles.landingLink} href={landing.playlistHref} target="_blank" rel="noopener">
          {words.listOpen}
        </a>
      </div>
    </div>
  );
}

/**
 * The band over the top edge: the clickable title at the start, and the two steps through the list,
 * Save to playlist and Watch later at the end. On the `player` stage it rides *inside* the player
 * surface (the `overlay` prop) so it survives fullscreen, and fades on the chrome's own clock — a
 * faded band is `inert` for the chrome's reason: a click aimed at the video must not land on an
 * invisible control. A load that failed
 * pins it up regardless: the error box has the picture, so there is nothing left for the auto-hide
 * to uncover. On every other stage (`problem`, `unplayable`, `upcoming`, `progressive`, and the
 * swapped-in foreign player) there is no chrome to fade with, so it is mounted `visible` and stays
 * up for as long as that stage does — and `flow` there as well, taking a row of its own at the top
 * of the frame instead of a layer over it, since a band that never fades would cover the arm under
 * it for good.
 *
 * The band itself is not hit-testable (`pointer-events` in the stylesheet) — only the title and the
 * button row are, so a click on the picture near the top edge still reaches the player. Both gesture
 * ends stop propagating there, so pressing any of them is not *also* a tap on the video.
 *
 * The three pointer handlers are `PlayerChrome`'s own set, and each one is load-bearing here for
 * the same reason it is there: an auto-hide that measures "the pointer stopped" must not hide a
 * control out from under a pointer that has arrived at one. **`onPointerEnter` in particular** —
 * without it the band flickered on a ~2.5 s cycle under a resting pointer (measured): the fade
 * makes the band `inert`, the browser re-hits and fires `pointerleave` at it, that hands the clock
 * back, the band returns, `pointerenter` fires with nothing listening, and it fades again. The
 * press that arrives mid-cycle lands on the video — which is the auto-hide's oldest trap
 * ([web-ui.md](../../../../../docs/specs/web-ui.md)), not a new one.
 *
 * The title link opens the video's /watch page in a **new tab** — the page usually sits in somebody
 * else's `<iframe>`, and navigating that iframe would cram the whole app into it.
 *
 * Watch later is the watch page's own button (2026-08-06), in the grid card's `corner` face: the
 * same one-press toggle, the same picker for a reader who has not appointed a playlist yet, and the
 * same silence when signed out — where a press could only open a sign-in, and the sign-in link in
 * the picker would navigate the host page's iframe. **Save to playlist sits to its left**, the same
 * face at the same size and under all the same rules, so the band offers what the watch page's
 * actions row does in the two presses an embed has room for.
 *
 * The two steps through the list lead that group where the address carries one (2026-09-19). They
 * are here rather than on the picture because the picture is what the reader came to watch, and the
 * band is already the one strip on this page that is ours. They are on the band's own clock with
 * everything else in it, which on the `player` arm is the chrome's, so they come up with the bar and
 * with a tap. `controls=0` leaves `step` `null` (`model.ts`), so an embedder who asked for a bare
 * picture keeps the title and the two library buttons and no transport. On the arms that draw no
 * player — a failed load, a premiere, a foreign player — the steps stay: a video that never loads
 * also never ends, so the advance never fires and these are the only way past it.
 */
function TopBand({
  binding,
  title,
  href,
  step = null,
  visible,
  flow = false,
}: {
  binding: WatchBinding;
  title: string;
  href: string;
  /** Where the two steps lead, or `null` where there is no list and none is drawn. */
  step?: EmbedStep | null;
  visible: boolean;
  /** A row of its own rather than a layer over the picture — every arm but `player`. */
  flow?: boolean;
}) {
  const { handlers } = binding;
  const words = useCopy().text.watch.playlist;
  /*
   * Pinned up for as long as either button's dialog is, because the auto-hide *can* run out under an
   * open one — measured on the state a **touch** tap leaves behind: the pointer is destroyed on
   * lift, so the band gets a `pointerleave`, `pointerOverChrome` clears, nothing is `:focus-visible`,
   * and 2.5 s later the chrome is gone. The dialog itself survives that (see the stylesheet for what
   * a top layer does and does not inherit); the *button under it* would not, so closing the dialog
   * would hand focus back to something hidden and `inert`. One flag for both buttons: a modal
   * dialog is the only thing on screen while it is up, so two cannot be open at once.
   */
  const [dialogOpen, setDialogOpen] = useState(false);
  const shown = visible || dialogOpen;

  return (
    <div
      className={cx(styles.topBand, flow && styles.topBandFlow, !shown && styles.topBandHidden)}
      inert={!shown}
      onPointerDown={(event) => event.stopPropagation()}
      onPointerUp={(event) => event.stopPropagation()}
      onPointerEnter={handlers.chromeActivity}
      onPointerMove={(event) => {
        event.stopPropagation();
        handlers.chromeActivity();
      }}
      onPointerLeave={handlers.pointerActivity}
    >
      <a className={styles.title} href={href} target="_blank" rel="noopener">
        {title}
      </a>
      <span className={styles.actions}>
        {step === null ? null : (
          <>
            <EntryStep href={step.previousHref} label={words.previousEntry} direction="back" />
            <EntryStep href={step.nextHref} label={words.nextEntry} direction="forward" />
          </>
        )}
        <AddToPlaylist
          face="corner"
          cornerSize="md"
          videoId={binding.videoId}
          videoTitle={title}
          onDialogOpenChange={setDialogOpen}
        />
        <WatchLaterButton
          face="corner"
          cornerSize="md"
          videoId={binding.videoId}
          videoTitle={title}
          onPickerOpenChange={setDialogOpen}
        />
      </span>
    </div>
  );
}

/**
 * One step through the list, back or forward, in the band's button row.
 *
 * A **link** while there is somewhere to go and a disabled button at the ends of the list, which is
 * the pairing the watch page's panel draws for the same two reasons: a step through a list is a
 * navigation, so middle-click, ⌘-click and "copy link address" go on working, and there is no such
 * thing as a disabled anchor. The end of a list goes grey in place instead of disappearing, so the
 * other three buttons keep their positions.
 *
 * `media` at `md`, the face and size the band's other two buttons wear: this row sits on video, and
 * the glyphs have to read over whatever frame is behind them.
 */
function EntryStep({
  href,
  label,
  direction,
}: {
  readonly href: string | null;
  readonly label: string;
  readonly direction: 'back' | 'forward';
}) {
  const glyph = <StepGlyph direction={direction} />;
  if (href === null) {
    return (
      <Button variant="media" size="md" icon label={label} title={label} disabled>
        {glyph}
      </Button>
    );
  }
  return (
    <Link
      className={buttonClass({ variant: 'media', size: 'md', icon: true })}
      href={href}
      ariaLabel={label}
      title={label}
    >
      {glyph}
    </Link>
  );
}

/**
 * The transport step: a triangle against a bar, the same picture the watch page's playlist panel
 * and the corner player step with. Drawn here as well rather than shared, which is this layer's
 * rule for a glyph one component draws.
 */
function StepGlyph({ direction }: { readonly direction: 'back' | 'forward' }) {
  return (
    <svg viewBox="0 0 24 24" width="18" height="18" aria-hidden="true" focusable="false">
      <g transform={direction === 'forward' ? undefined : 'translate(24 0) scale(-1 1)'}>
        <path fill="currentColor" d="M6 5.5 15.5 12 6 18.5z" />
        <path
          fill="none"
          stroke="currentColor"
          strokeWidth="1.9"
          strokeLinecap="round"
          d="M17.8 5.5v13"
        />
      </g>
    </svg>
  );
}
